import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Send, X, Sparkles, MessageCircle, MapPin, Check } from "lucide-react";
import { sendChat, api } from "@/lib/api";
import { fetchLocationHint, localTimeOfDay, formatLocationHint, detectCultureFromName } from "@/lib/locale";
import { downloadPackagePDF } from "@/lib/packagePdf";
import { trackLead } from "@/lib/analytics";
import { toast } from "sonner";

// 6 rotating national-brand personas — Indianised names + professional placeholder portraits.
// Photos can be swapped for real team photos later without code changes elsewhere.
const PERSONAS = [
  {
    name: "Savi",
    tagline: "Senior Concierge · Honeymoons & Beaches",
    avatar: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=200&h=200&fit=crop&q=85",
  },
  {
    name: "Manoj",
    tagline: "Senior Concierge · International Holidays",
    avatar: "https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?w=200&h=200&fit=crop&q=85",
  },
  {
    name: "Kamala",
    tagline: "Senior Concierge · Heritage & Culture",
    avatar: "https://images.unsplash.com/photo-1580489944761-15a19d654956?w=200&h=200&fit=crop&q=85",
  },
  {
    name: "Anil",
    tagline: "Senior Concierge · Luxury & NRI Travel",
    avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=200&h=200&fit=crop&q=85",
  },
  {
    name: "Priya",
    tagline: "Senior Concierge · Family Escapes",
    avatar: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=200&h=200&fit=crop&q=85",
  },
  {
    name: "Vikram",
    tagline: "Senior Concierge · Adventure & Mountains",
    avatar: "https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=200&h=200&fit=crop&q=85",
  },
];

const WHATSAPP_NUMBER = "919501030117";

// Minimum time the "Typing…" indicator stays visible — set to 1.5s for perceived speed
// while still feeling human (user explicitly prioritised speed).
const TYPING_MIN_MS = 1500;

// Tiny unique ID factory for message bubbles — used as React key so
// streaming-in replies don't rely on array index.
let _msgSeq = 0;
const _nextId = () => `m${Date.now()}_${++_msgSeq}`;

// Strip any stray JSON / code blocks that might leak into chat from the AI. The AI is
// prompted to return JSON but we only render the `reply` field — this guard is a
// belt-and-braces cleanup in case a raw ```json block ever slips through.
function sanitizeText(text) {
  if (!text) return "";
  let out = String(text);
  // Remove ``` blocks (```json...``` or ```...```)
  out = out.replace(/```[\s\S]*?```/g, "");
  // If the remaining text is a pure JSON object, try to pick out its "reply" field
  const trimmed = out.trim();
  if (trimmed.startsWith("{") && trimmed.endsWith("}")) {
    try {
      const parsed = JSON.parse(trimmed);
      if (parsed && typeof parsed.reply === "string") return parsed.reply.trim();
    } catch { /* fall through */ }
  }
  // Remove inline {...} that looks like a lead JSON object
  out = out.replace(/\{\s*"(?:reply|lead|complete)"[\s\S]*?\}\s*$/m, "");
  return out.trim();
}

function buildWhatsAppUrl(lead, destination, pkg, opener, pdfUrl) {
  const parts = [
    opener || "Hello Vacations Pro — please share my custom PDF itinerary on WhatsApp.",
    "",
    `Name: ${lead?.name || "—"}`,
    `Phone: ${lead?.phone || "—"}`,
    `Destination: ${lead?.destination || destination || "—"}`,
    `Travel date: ${lead?.travel_date || "—"}`,
    `Dates flexible: ${lead?.dates_flexible === true ? "Yes" : lead?.dates_flexible === false ? "No" : "—"}`,
    `Passengers: ${lead?.persons || "—"}`,
    `Hotel preference: ${lead?.hotel_preference || "—"}`,
    `Occasion: ${lead?.occasion || "—"}`,
  ];
  if (lead?.email) parts.push(`Email: ${lead.email}`);
  if (lead?.preferences) parts.push(`Other preferences: ${lead.preferences}`);
  if (pkg?.title) {
    parts.push("", `Package: ${pkg.title}`);
    if (pkg.estimated_budget_inr) parts.push(`Est. budget: ${pkg.estimated_budget_inr}`);
  }
  if (pdfUrl) {
    parts.push("", `📄 PDF itinerary: ${pdfUrl}`);
  } else if (pkg?.title) {
    parts.push("", "(PDF has been downloaded — please attach it when you send this message.)");
  }
  // Use api.whatsapp.com/send (more reliable across iOS Safari + Android + desktop)
  // than the older wa.me short-link, especially when launched programmatically without
  // a direct user gesture (auto-open fallback on chat-complete).
  return `https://api.whatsapp.com/send?phone=${WHATSAPP_NUMBER}&text=${encodeURIComponent(parts.join("\n"))}`;
}

// Funnel progress — 7 mandatory fields.
// Returns 0-100. Used by the thin progress bar at the top of the chat.
function computeProgress(lead) {
  if (!lead) return 0;
  const keys = ["name", "phone", "destination", "travel_date", "persons", "hotel_preference", "occasion"];
  const filled = keys.filter((k) => lead[k] && String(lead[k]).trim() !== "").length;
  return Math.round((filled / keys.length) * 100);
}

export default function AIChat({ open, onOpenChange, destination, mood }) {
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [lead, setLead] = useState(null);
  const [complete, setComplete] = useState(false);
  const [fallbackMode, setFallbackMode] = useState(null); // "contact_form" | null
  const [honeypot, setHoneypot] = useState(""); // legit users never touch this

  // stage: 'chat' -> 'package'
  const [stage, setStage] = useState("chat");
  const [pkg, setPkg] = useState(null);
  const [pkgLoading, setPkgLoading] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [sendingAll, setSendingAll] = useState(false);

  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open || stage !== "chat") return;
    const last = messages[messages.length - 1];
    if (last?.role === "assistant" && !loading && inputRef.current) {
      const t = setTimeout(() => inputRef.current?.focus(), 80);
      return () => clearTimeout(t);
    }
  }, [messages, loading, open, stage]);

  const [locale, setLocale] = useState({ location_hint: "", local_time_hint: "" });
  useEffect(() => {
    if (!open) return;
    let active = true;
    (async () => {
      const info = await fetchLocationHint();
      if (!active) return;
      setLocale({
        location_hint: formatLocationHint(info),
        local_time_hint: localTimeOfDay(info.timezone),
      });
    })();
    return () => { active = false; };
  }, [open]);

  const personaRef = useRef(PERSONAS[Math.floor(Math.random() * PERSONAS.length)]);
  const [personaTick, setPersonaTick] = useState(0);
  const persona = personaRef.current;
  const initiatedRef = useRef(false);
  const userReplyCount = messages.filter((m) => m.role === "user").length;

  useEffect(() => {
    if (open) {
      personaRef.current = PERSONAS[Math.floor(Math.random() * PERSONAS.length)];
      setPersonaTick((t) => t + 1);
      if (!initiatedRef.current && messages.length === 0) {
        initiatedRef.current = true;
        void initiate();
      }
    } else {
      initiatedRef.current = false;
      setSessionId(null);
      setMessages([]);
      setLead(null);
      setComplete(false);
      setInput("");
      setStage("chat");
      setPkg(null);
      setFallbackMode(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, destination]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading, stage, pkg]);

  // Chat-complete → jump straight to package (OTP removed) + open WhatsApp with the lead
  // payload pre-filled. We use TWO redundant paths so the user is NEVER left at a dead end:
  //   1. window.open(...) — works when the chat completion is the direct result of a click
  //      (most modern browsers grant the auto-open permission).
  //   2. window.location.assign(...) — used as a 1500ms fallback when window.open returned
  //      null (popup blocked) so the user is still sent to WhatsApp on the same tab.
  // Either way the message contains Name, Destination, Dates, Pax, Hotel, Occasion + any
  // package details — no lead can be lost even if the backend CallMeBot key is empty.
  useEffect(() => {
    if (complete && stage === "chat") {
      setStage("package");
      void generatePackage();
      const t = setTimeout(() => {
        try {
          const wa = buildWhatsAppUrl(lead, destination, null, undefined, null);
          trackLead({
            source: "chat_auto_complete",
            destination: lead?.destination || destination,
            extra: { auto_opened: true },
          });
          toast.success("Opening WhatsApp with your details — just tap Send!", { duration: 5500 });
          const win = window.open(wa, "_blank", "noopener,noreferrer");
          if (!win || win.closed || typeof win.closed === "undefined") {
            // Popup blocked → fall back to same-tab navigation. We give the user a
            // chance to read the toast first (1.5s) before redirecting.
            setTimeout(() => { window.location.assign(wa); }, 1500);
          }
        } catch (err) {
          console.warn("auto-wa open failed", err);
          // Last-resort fallback — show a persistent CTA in the chat surface.
          try {
            const wa = buildWhatsAppUrl(lead, destination, null, undefined, null);
            toast.error(
              "Tap here to continue on WhatsApp",
              { duration: 12000, action: { label: "Open WhatsApp", onClick: () => window.open(wa, "_blank") } }
            );
          } catch { /* truly nothing we can do */ }
        }
      }, 900);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [complete, stage]);

  // Wrap any chat request so we always show the typing indicator for at least
  // TYPING_MIN_MS (feels human, not AI).
  const sendWithTypingDelay = async (promise) => {
    const started = Date.now();
    try {
      const result = await promise;
      const elapsed = Date.now() - started;
      const wait = Math.max(TYPING_MIN_MS - elapsed, 0);
      if (wait) await new Promise((r) => setTimeout(r, wait));
      return result;
    } catch (e) {
      const elapsed = Date.now() - started;
      const wait = Math.max(TYPING_MIN_MS - elapsed, 0);
      if (wait) await new Promise((r) => setTimeout(r, wait));
      throw e;
    }
  };

  const initiate = async () => {
    setLoading(true);
    const activePersona = personaRef.current;
    try {
      const openingMsg = destination
        ? (mood
            ? `I'd like a ${mood.toLowerCase()} themed trip to ${destination}.`
            : `I just saw the ${destination} spotlight and I'm impressed! Help me plan a trip here.`)
        : "Hello, I'd like to plan a trip.";
      const data = await sendWithTypingDelay(sendChat({
        message: openingMsg,
        destination,
        persona: activePersona.name,
        mood,
        culture_hint: "unknown",
        location_hint: locale.location_hint,
        local_time_hint: locale.local_time_hint,
        hp: honeypot,
      }));
      setSessionId(data.session_id);
      if (data.fallback_mode === "contact_form") {
        setFallbackMode("contact_form");
        setMessages([{ id: _nextId(), role: "assistant", content: data.reply }]);
      } else {
        setMessages([
          { id: _nextId(), role: "user", content: openingMsg },
          { id: _nextId(), role: "assistant", content: sanitizeText(data.reply) },
        ]);
      }
      setLead(data.lead);
      setComplete(data.lead_complete);
    } catch (err) {
      console.warn("AIChat initiate failed", err);
      toast.error("Couldn't reach the concierge. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    const text = input.trim();
    if (!text || loading) return;
    setInput("");
    setMessages((p) => [...p, { id: _nextId(), role: "user", content: text }]);
    setLoading(true);
    const activePersona = personaRef.current;
    try {
      const culture_hint = lead?.name ? detectCultureFromName(lead.name) : detectCultureFromName(text);
      const data = await sendWithTypingDelay(sendChat({
        session_id: sessionId,
        message: text,
        destination,
        persona: activePersona.name,
        mood,
        culture_hint,
        location_hint: locale.location_hint,
        local_time_hint: locale.local_time_hint,
        hp: honeypot,
      }));
      setSessionId(data.session_id);
      if (data.fallback_mode === "contact_form") {
        setFallbackMode("contact_form");
        setMessages((p) => [...p, { id: _nextId(), role: "assistant", content: data.reply }]);
      } else {
        setMessages((p) => [...p, { id: _nextId(), role: "assistant", content: sanitizeText(data.reply) }]);
      }
      setLead(data.lead);
      setComplete(data.lead_complete);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Connection lost. Try again.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const generatePackage = async () => {
    setPkgLoading(true);
    try {
      const { data } = await api.post("/package", { session_id: sessionId });
      setPkg(data.package);
    } catch (err) {
      console.warn("Package draft failed", err);
      toast.error("Could not draft the package. Please try again.");
    } finally {
      setPkgLoading(false);
    }
  };

  const progress = computeProgress(lead);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-2xl p-0 overflow-hidden border-0 bg-transparent shadow-none"
        data-testid="ai-chat-modal"
      >
        <div className="vp-glass rounded-2xl overflow-hidden border border-white/50 shadow-[0_30px_80px_rgba(0,0,0,0.35)]">
          {/* Thin progress bar at very top of chat */}
          {stage === "chat" && (
            <div className="h-1 bg-black/5" data-testid="chat-progress-track">
              <div
                className="h-full bg-gradient-to-r from-[var(--vp-secondary)] to-[var(--vp-accent)] transition-all duration-500"
                style={{ width: `${progress}%` }}
                data-testid="chat-progress-fill"
              />
            </div>
          )}

          {/* Header */}
          <div className="flex items-center gap-4 p-5 border-b border-black/10 bg-white/60">
            <img
              src={persona.avatar}
              alt={persona.name}
              className="w-12 h-12 rounded-full object-cover ring-2 ring-[var(--vp-accent)]"
              data-testid="concierge-avatar"
            />
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <span className="font-serif-vp text-xl text-[var(--vp-primary)]" data-testid="concierge-name">
                  {persona.name}
                </span>
                <Sparkles className="w-3.5 h-3.5 text-[var(--vp-secondary)]" strokeWidth={1.5} />
              </div>
              <span className="text-xs text-[var(--vp-muted)] tracking-wider uppercase">
                {persona.tagline}
              </span>
            </div>
            <button
              onClick={() => onOpenChange(false)}
              className="p-2 hover:bg-black/5 rounded-full"
              data-testid="ai-chat-close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Body */}
          {stage !== "package" && (
            <div
              ref={scrollRef}
              className="bg-[var(--vp-bg)]/80 max-h-[45vh] min-h-[280px] overflow-y-auto p-6 space-y-4"
              data-testid="ai-chat-messages"
            >
              {messages.length === 0 && loading && (
                <div className="text-sm text-[var(--vp-muted)] italic">
                  {persona.name} is preparing a warm welcome…
                </div>
              )}
              {messages.map((m) => (
                <Bubble key={m.id} role={m.role} content={m.content} />
              ))}
              {loading && messages.length > 0 && <Typing name={persona.name} />}
            </div>
          )}

          {/* Lead strip — HIDDEN on mobile (clutters), visible sm+ */}
          {lead && stage === "chat" && !fallbackMode && (
            <div
              className="hidden sm:grid px-6 py-3 border-t border-black/10 bg-white/70 grid-cols-4 gap-3 text-xs"
              data-testid="ai-lead-summary"
            >
              <Field label="Name" value={lead.name} />
              <Field label="Destination" value={lead.destination} />
              <Field label="Dates" value={lead.travel_date} />
              <Field label="Phone" value={lead.phone} />
            </div>
          )}

          {/* Contact-form fallback (global budget cap reached) */}
          {fallbackMode === "contact_form" && (
            <ContactFormPanel onSent={() => onOpenChange(false)} />
          )}

          {/* WhatsApp handoff after 3 replies */}
          {stage === "chat" && !fallbackMode && userReplyCount >= 3 && (
            <a
              href={buildWhatsAppUrl(lead, destination, pkg)}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackLead({ source: "midchat_whatsapp", destination: lead?.destination || destination })}
              className="flex items-center justify-between gap-3 px-6 py-3.5 bg-[#25D366]/95 hover:bg-[#1ebe5d] text-white border-t border-black/10 transition-colors"
              data-testid="whatsapp-handoff-btn"
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                  <MessageCircle className="w-4 h-4" />
                </div>
                <div>
                  <div className="text-sm font-medium">Prefer WhatsApp?</div>
                  <div className="text-[11px] text-white/85">+91 95010 30117 · details pre-filled</div>
                </div>
              </div>
              <span className="text-xs tracking-[0.18em] uppercase font-medium">Open</span>
            </a>
          )}

          {/* Input (chat stage) */}
          {stage === "chat" && !fallbackMode && (
            <form
              onSubmit={handleSend}
              className="p-4 bg-white/80 border-t border-black/10 flex items-center gap-2"
              data-testid="ai-chat-form"
            >
              {/* Honeypot — invisible to humans, visible to bots */}
              <input
                type="text"
                name="hp"
                tabIndex={-1}
                autoComplete="off"
                value={honeypot}
                onChange={(e) => setHoneypot(e.target.value)}
                className="hidden"
                aria-hidden="true"
              />
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Type your reply…"
                className="flex-1 bg-white border border-[var(--vp-border)] rounded-full px-5 py-3 text-sm outline-none focus:border-[var(--vp-primary)]"
                data-testid="ai-chat-input"
                disabled={loading}
                autoFocus
              />
              <button
                type="submit"
                disabled={loading || !input.trim()}
                className="rounded-full bg-[var(--vp-primary)] hover:bg-[#0f2318] text-white p-3 disabled:opacity-40"
                data-testid="ai-chat-send"
              >
                <Send className="w-4 h-4" />
              </button>
            </form>
          )}

          {/* Package stage */}
          {stage === "package" && (
            <div className="bg-white max-h-[60vh] overflow-y-auto" data-testid="package-panel">
              {pkgLoading ? (
                <div className="p-10 text-center text-[var(--vp-muted)] text-sm">
                  <Sparkles className="w-6 h-6 mx-auto mb-3 animate-pulse text-[var(--vp-secondary)]" />
                  Curating your personalised package…
                </div>
              ) : pkg ? (
                <div className="space-y-5">
                  {pkg.hero_image && (
                    <div className="relative h-44 md:h-56 overflow-hidden" data-testid="package-hero">
                      <img src={pkg.hero_image} alt={pkg.title || "Package"} className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                      <div className="absolute bottom-3 left-5 right-5">
                        <span className="vp-label text-white/90">VERIFIED · PACKAGE DRAFT</span>
                      </div>
                    </div>
                  )}
                  <div className="px-6 pb-6 space-y-5">
                  {!pkg.hero_image && (
                    <div className="flex items-center gap-2 text-[var(--vp-secondary)]">
                      <Check className="w-4 h-4" />
                      <span className="vp-label text-[var(--vp-secondary)]">VERIFIED · PACKAGE DRAFT</span>
                    </div>
                  )}
                  <h3 className="font-serif-vp text-3xl text-[var(--vp-primary)] leading-tight" data-testid="package-title">
                    {pkg.title}
                  </h3>
                  <p className="text-sm text-[var(--vp-muted)] leading-relaxed">{pkg.summary}</p>

                  <div className="space-y-4">
                    {(pkg.days || []).map((d) => (
                      <div key={d.day} className="bg-white border border-[var(--vp-border)] rounded-xl overflow-hidden">
                        {d.image && (
                          <div className="relative h-28 overflow-hidden">
                            <img src={d.image} alt={`Day ${d.day}`} className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                            <div className="absolute bottom-2 left-4 flex items-center gap-2 text-white">
                              <span className="vp-label text-white/95">DAY {d.day} · {d.theme}</span>
                            </div>
                          </div>
                        )}
                        <div className="p-4">
                          {!d.image && (
                            <div className="flex items-center gap-2 mb-2">
                              <MapPin className="w-3.5 h-3.5 text-[var(--vp-primary)]" />
                              <span className="vp-label">DAY {d.day} · {d.theme}</span>
                            </div>
                          )}
                          <ul className="text-sm text-[var(--vp-text)] space-y-1">
                            {(d.highlights || []).map((h, i) => (
                              <li key={i} className="flex gap-2">
                                <span className="text-[var(--vp-accent)]">—</span>
                                <span>{h}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    ))}
                  </div>

                  {pkg.flight_suggestion && (
                    <div className="bg-[var(--vp-bg)] border border-[var(--vp-border)] rounded-xl p-4" data-testid="package-flight">
                      <span className="vp-label block mb-1.5 text-[var(--vp-primary)]">FLIGHT ROUTING</span>
                      <div className="text-sm text-[var(--vp-text)] font-mono">
                        {pkg.flight_suggestion}
                      </div>
                      {pkg.origin_airport && (
                        <div className="text-[10px] tracking-wider uppercase text-[var(--vp-muted)] mt-1">
                          From {pkg.origin_airport}
                        </div>
                      )}
                    </div>
                  )}

                  {/* 3-tier hotel options — backend returns pkg.hotel_tiers as [{tier,name,nightly,highlights:[]}] */}
                  {Array.isArray(pkg.hotel_tiers) && pkg.hotel_tiers.length > 0 && (
                    <div data-testid="package-hotels">
                      <span className="vp-label block mb-2">HOTEL OPTIONS</span>
                      <div className="grid md:grid-cols-3 gap-3">
                        {pkg.hotel_tiers.map((h, i) => (
                          <div key={i} className="border border-[var(--vp-border)] rounded-lg p-3 bg-white">
                            <div className="vp-label text-[10px] text-[var(--vp-secondary)] mb-1">{h.tier}</div>
                            <div className="font-serif-vp text-base text-[var(--vp-primary)] leading-snug">
                              {h.name}
                            </div>
                            {h.nightly && (
                              <div className="text-xs text-[var(--vp-muted)] mt-1">{h.nightly}</div>
                            )}
                            {Array.isArray(h.highlights) && h.highlights.length > 0 && (
                              <ul className="text-[11px] text-[var(--vp-text)] mt-2 space-y-0.5">
                                {h.highlights.slice(0, 3).map((x, j) => (
                                  <li key={j}>— {x}</li>
                                ))}
                              </ul>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {pkg.inclusions?.length > 0 && (
                    <div>
                      <span className="vp-label block mb-2">INCLUSIONS</span>
                      <div className="flex flex-wrap gap-2">
                        {pkg.inclusions.map((inc, i) => (
                          <span key={i} className="text-xs bg-[var(--vp-bg)] border border-[var(--vp-border)] px-3 py-1.5 rounded-full">
                            {inc}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {pkg.estimated_budget_inr && (
                    <div className="flex items-center justify-between border-t pt-4 border-[var(--vp-border)]">
                      <span className="vp-label">ESTIMATED BUDGET</span>
                      <span className="font-serif-vp text-2xl text-[var(--vp-primary)]">{pkg.estimated_budget_inr}</span>
                    </div>
                  )}

                  {/* Primary action — generate PDF, upload to GridFS, download locally, open WhatsApp with a direct link */}
                  <button
                    type="button"
                    disabled={sendingAll}
                    onClick={async () => {
                      if (sendingAll) return;
                      setSendingAll(true);
                      try {
                        // 1. Generate the PDF blob (not auto-downloaded yet).
                        const result = await downloadPackagePDF({
                          pkg, lead, destination, returnBlob: true,
                        });
                        if (!result || !result.blob) throw new Error("Empty PDF");
                        const { blob, filename } = result;

                        // 2. Upload to backend → GridFS → public URL.
                        let pdfUrl = null;
                        try {
                          const form = new FormData();
                          form.append("file", blob, filename);
                          form.append("session_id", sessionId || "anon");
                          const { data } = await api.post("/pdf/upload", form, {
                            headers: { "Content-Type": "multipart/form-data" },
                          });
                          if (data?.url) {
                            pdfUrl = `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
                          }
                        } catch (upErr) {
                          console.warn("PDF upload failed — falling back to local attach", upErr);
                        }

                        // 3. Also download locally for the traveller.
                        const a = document.createElement("a");
                        a.href = URL.createObjectURL(blob);
                        a.download = filename;
                        document.body.appendChild(a);
                        a.click();
                        document.body.removeChild(a);
                        setTimeout(() => URL.revokeObjectURL(a.href), 2000);

                        // 4. Open WhatsApp — include the hosted link when upload succeeded.
                        const wa = buildWhatsAppUrl(lead, destination, pkg, undefined, pdfUrl);
                        trackLead({
                          source: "package_send_whatsapp",
                          destination: lead?.destination || destination,
                          extra: { has_pdf_url: !!pdfUrl },
                        });
                        window.open(wa, "_blank", "noopener");
                        toast.success(
                          pdfUrl
                            ? "PDF uploaded + downloaded. WhatsApp opened with a direct link."
                            : "PDF downloaded — attach it manually in WhatsApp.",
                          { duration: 6000 }
                        );
                      } catch (err) {
                        console.error(err);
                        toast.error("Couldn't generate PDF — try again");
                      } finally {
                        setSendingAll(false);
                      }
                    }}
                    className="w-full flex items-center justify-center gap-3 bg-[#25D366] hover:bg-[#1ebe5d] text-white rounded-xl py-4 transition-colors disabled:opacity-60"
                    data-testid="package-send-whatsapp-pdf-btn"
                  >
                    <MessageCircle className="w-5 h-5" />
                    <span className="text-sm tracking-[0.1em] uppercase font-bold">
                      {sendingAll ? "Preparing…" : "Send on WhatsApp (PDF attached)"}
                    </span>
                  </button>

                  {/* Secondary — just download the PDF */}
                  <button
                    onClick={async () => {
                      if (pdfBusy) return;
                      setPdfBusy(true);
                      try {
                        await downloadPackagePDF({ pkg, lead, destination });
                        toast.success("PDF downloaded");
                      } catch (err) {
                        console.error(err);
                        toast.error("Couldn't generate PDF — try again");
                      } finally {
                        setPdfBusy(false);
                      }
                    }}
                    disabled={pdfBusy}
                    className="w-full flex items-center justify-center gap-2 bg-white border border-[var(--vp-border)] text-[var(--vp-primary)] hover:bg-[var(--vp-bg)] rounded-xl py-3 transition-colors disabled:opacity-50"
                    data-testid="package-download-btn"
                  >
                    <Check className="w-4 h-4" />
                    <span className="text-xs tracking-[0.12em] uppercase font-semibold">
                      {pdfBusy ? "…" : "Download PDF only"}
                    </span>
                  </button>
                  </div>
                </div>
              ) : null}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function renderWithImages(text) {
  if (!text) return null;
  const re = /!\[([^\]]*)\]\(([^)\s]+)\)/g;
  const out = [];
  let last = 0;
  let m;
  let idx = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) {
      const chunk = text.slice(last, m.index);
      if (chunk) out.push(<span key={`t${idx++}`}>{chunk}</span>);
    }
    const alt = m[1] || "Vacations Pro";
    const src = m[2];
    out.push(
      <img
        key={`i${idx++}`}
        src={src}
        alt={alt}
        loading="lazy"
        className="block w-full max-w-sm my-2 rounded-lg border border-[var(--vp-border)] object-cover aspect-[4/3]"
        onError={(e) => { e.currentTarget.style.display = "none"; }}
        data-testid="chat-inline-image"
      />
    );
    last = m.index + m[0].length;
  }
  if (last < text.length) {
    const chunk = text.slice(last);
    if (chunk) out.push(<span key={`t${idx++}`}>{chunk}</span>);
  }
  return out;
}

function Bubble({ role, content }) {
  const isUser = role === "user";
  const text = isUser ? content : sanitizeText(content);
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div
        className={`max-w-[80%] px-4 py-3 rounded-2xl text-sm leading-relaxed font-sans-vp whitespace-pre-wrap ${
          isUser
            ? "bg-[var(--vp-primary)] text-white rounded-br-sm"
            : "bg-white text-[var(--vp-text)] border border-[var(--vp-border)] rounded-bl-sm"
        }`}
        data-testid={`chat-bubble-${role}`}
      >
        {isUser ? text : renderWithImages(text)}
      </div>
    </div>
  );
}

// NOTE: QUALIFY_QUESTIONS + QualifyBlock removed in v14 — those fields are now
// collected conversationally during the chat funnel (hotel_preference, occasion, etc.)
// and flow straight into the WhatsApp handover + PDF.

function Typing({ name }) {
  return (
    <div className="flex flex-col items-start gap-1">
      <span className="text-[10px] tracking-wider uppercase text-[var(--vp-muted)] ml-1">
        {name} is typing…
      </span>
      <div className="bg-white border border-[var(--vp-border)] rounded-2xl px-4 py-3 flex gap-1.5">
        <span className="w-1.5 h-1.5 rounded-full bg-[var(--vp-muted)] animate-bounce [animation-delay:-0.3s]" />
        <span className="w-1.5 h-1.5 rounded-full bg-[var(--vp-muted)] animate-bounce [animation-delay:-0.15s]" />
        <span className="w-1.5 h-1.5 rounded-full bg-[var(--vp-muted)] animate-bounce" />
      </div>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div className="min-w-0">
      <div className="vp-label text-[10px] mb-0.5">{label}</div>
      <div className="text-[var(--vp-text)] truncate">
        {value || <span className="text-[var(--vp-muted)]">—</span>}
      </div>
    </div>
  );
}

function UnlockPanel() { return null; }  // deprecated — Priority Unlock removed per spec

// Fallback lead capture shown when the AI has been rate-limited for the day.
// Plain fields, no LLM calls — always works, even if the LLM budget is hit.
function ContactFormPanel({ onSent }) {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [destination, setDestination] = useState("");
  const [message, setMessage] = useState("");
  const [hp, setHp] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !phone.trim()) {
      toast.error("Please share your name and WhatsApp number.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/contact", { name, phone, destination, message, hp });
      toast.success("Thanks — we'll WhatsApp you within 30 minutes.");
      if (onSent) onSent();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't submit. Try WhatsApp directly.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      className="p-5 md:p-6 bg-white/90 border-t border-black/10 space-y-3"
      data-testid="contact-form-panel"
    >
      <input type="text" name="hp" value={hp} onChange={(e) => setHp(e.target.value)}
             className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />
      <input
        type="text" placeholder="Your name"
        value={name} onChange={(e) => setName(e.target.value)}
        className="w-full bg-white border border-[var(--vp-border)] rounded-md px-4 py-3 text-sm"
        data-testid="contact-name-input"
      />
      <input
        type="tel" placeholder="WhatsApp number (10-digit)"
        value={phone} onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
        className="w-full bg-white border border-[var(--vp-border)] rounded-md px-4 py-3 text-sm"
        data-testid="contact-phone-input"
      />
      <input
        type="text" placeholder="Destination (e.g. Bali, Maldives, Dubai)"
        value={destination} onChange={(e) => setDestination(e.target.value)}
        className="w-full bg-white border border-[var(--vp-border)] rounded-md px-4 py-3 text-sm"
        data-testid="contact-dest-input"
      />
      <textarea
        rows={2} placeholder="Anything else? (dates, occasion, budget)"
        value={message} onChange={(e) => setMessage(e.target.value)}
        className="w-full bg-white border border-[var(--vp-border)] rounded-md px-4 py-3 text-sm"
        data-testid="contact-msg-input"
      />
      <button
        type="submit" disabled={busy}
        className="w-full vp-btn-primary text-sm py-3.5 disabled:opacity-50"
        data-testid="contact-submit-btn"
      >
        {busy ? "Sending…" : "Request a callback"}
      </button>
    </form>
  );
}
